import React from "react";
import { Link } from "react-router-dom";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import { listOwnProviderProducts } from "../services/providerProductService";
import { listProviderOrders } from "../services/wholesaleService";
import { listConnectedSellers } from "../services/connectedSellersService";
import { getOwnStorefront } from "../services/storefrontService";
import { buildProviderWorkspace, PROVIDER_ROUTES } from "../providerWorkspace";
import PageShell, { useLoad } from "./PageShell";
import "./providerCenter.css";

const ok = (r) => r && !r.error && r.success !== false;

// Overview numbers come from the real Provider endpoints; a failing source shows "—" instead of a
// fabricated 0, and never blocks the rest of the dashboard.
const loadOverview = async (token) => {
  const [active, pending, sellers, storefront] = await Promise.all([
    listOwnProviderProducts(token, { status: "active", page: 1, limit: 1 }),
    listProviderOrders(token, { status: "pending", limit: 1 }),
    listConnectedSellers(token, { limit: 1, previewSize: 0 }),
    getOwnStorefront(token),
  ]);
  return {
    activeProducts: ok(active) ? active.total : null,
    pendingOrders: ok(pending) ? pending.total : null,
    connectedShops: ok(sellers) ? sellers.overview?.connectedShops ?? null : null,
    storefront: ok(storefront) ? storefront.storefront : null,
    profile: ok(storefront) ? storefront.profile : null,
  };
};

export const StorefrontBadge = ({ storefront }) => {
  if (!storefront) return <span className="pvc-badge pvc-badge--none">—</span>;
  if (storefront.isPublished) return <span className="pvc-badge pvc-badge--published"><i className="fas fa-check-circle" aria-hidden="true" />{text("h_pvc_status_published", "Published")}</span>;
  if (storefront.hasDraft) return <span className="pvc-badge pvc-badge--draft"><i className="fas fa-pen" aria-hidden="true" />{text("h_pvc_status_draft", "Draft")}</span>;
  return <span className="pvc-badge pvc-badge--none">{text("h_pvc_status_default", "Default storefront")}</span>;
};

export const WorkspaceTiles = ({ items }) => (
  <div className="pvc-grid">
    {items.map((item) => {
      const Icon = item.icon;
      const body = <>
        <span className="pvc-tile__icon" aria-hidden="true"><Icon /></span>
        <span className="pvc-tile__copy"><strong>{item.label}</strong><span>{item.description}</span></span>
      </>;
      if (item.status !== "active") {
        return (
          <div key={item.key} className="pvc-tile pvc-tile--inert" aria-disabled="true">
            <span className="pvc-tile__badge">{item.status === "coming_back" ? text("h_pvc_coming_back", "Coming back") : text("h_pvc_soon", "Soon")}</span>
            {body}
          </div>
        );
      }
      return <Link key={item.key} to={item.route} className="pvc-tile" data-workspace-key={item.key}>{body}</Link>;
    })}
  </div>
);

const Stat = ({ icon, label, value, to }) => {
  const content = <><span><i className={`fas ${icon}`} aria-hidden="true" />{label}</span><strong>{value ?? "—"}</strong></>;
  return to ? <Link className="pvc-stat" to={to}>{content}</Link> : <div className="pvc-stat">{content}</div>;
};

// /provider -- the central professional workspace of a Provider (and of a Combined Sell Center
// role acting as Provider). Every tile is a real, existing Provider route (providerWorkspace.js).
export default function ProviderCenter({ infoSource }) {
  const state = useLoad(loadOverview, []);
  const data = state.data || {};
  const items = buildProviderWorkspace(infoSource, { exclude: ["dashboard"] });
  const shopName = data.profile?.shop?.shopName;
  return (
    <PageShell
      infoSource={infoSource}
      titleElakay="providerProducts"
      icon="fa-warehouse"
      className="pvc-center"
      title={text("h_pvc_title", "Provider Center")}
      crumb={text("h_pvc_nav_dashboard", "Provider Dashboard")}
      subtitle={shopName
        ? text("h_pvc_sub_named", "{value0} — manage your products, storefront, wholesale and dropshipping business.", { value0: shopName })
        : text("h_pvc_sub", "Manage your products, storefront, wholesale and dropshipping business.")}
      actions={<>
        <Link to={PROVIDER_ROUTES.myShop} className="ctr__btn"><i className="fas fa-store" aria-hidden="true" />{text("h_pvc_nav_myshop", "My Provider Shop")}</Link>
        {items.some((i) => i.key === "addProduct" && i.status === "active") && <Link to={PROVIDER_ROUTES.addProduct} className="ctr__btn ctr__btn--primary"><i className="fas fa-plus" aria-hidden="true" />{text("h_pvc_nav_add", "Add Provider Product")}</Link>}
      </>}
      state={state}
    >
      <section aria-labelledby="pvc-overview-title">
        <div className="pvc-section-title"><h2 id="pvc-overview-title">{text("h_pvc_overview", "Business overview")}</h2></div>
        <div className="pvc-stats">
          <Stat icon="fa-box-open" label={text("h_pvc_stat_products", "Active products")} value={data.activeProducts} to={PROVIDER_ROUTES.products} />
          <Stat icon="fa-shopping-cart" label={text("h_pvc_stat_pending", "Pending wholesale orders")} value={data.pendingOrders} to={PROVIDER_ROUTES.orders} />
          <Stat icon="fa-users" label={text("h_pvc_stat_sellers", "Connected Sellers")} value={data.connectedShops} to={PROVIDER_ROUTES.connectedSellers} />
          <Link className="pvc-stat" to={PROVIDER_ROUTES.myShop}>
            <span><i className="fas fa-store" aria-hidden="true" />{text("h_pvc_stat_storefront", "Storefront")}</span>
            <strong className="pvc-stat__badge"><StorefrontBadge storefront={data.storefront} /></strong>
          </Link>
        </div>
      </section>
      <section aria-labelledby="pvc-workspace-title">
        <div className="pvc-section-title">
          <h2 id="pvc-workspace-title">{text("h_pvc_workspace", "Provider workspace")}</h2>
          <p>{text("h_pvc_workspace_sub", "Everything you need to run your Provider business.")}</p>
        </div>
        <WorkspaceTiles items={items} />
      </section>
    </PageShell>
  );
}
